import type { BaseQueryFn } from '@reduxjs/toolkit/query/react';
import type { AxiosError, AxiosRequestConfig } from 'axios';
import axios from 'axios';
import { HttpResponse } from '../../model/http-response';
import { ApiResponses } from '../../types/apiResponses';
import { checkIsNetworkCallSuccess, isInvisiblyResponse } from "../../helpers/response.helpers";
import { RootState } from "../redux-store";
import ErrorParsingService from "../../services/error-parsing/error-parsing.service";
import ErrorLogService from "../../services/error-log/error-log.service";
import { UsableError } from "../../services/error-parsing/error-parsing.types";
import { showNotification } from "../notification/notification.slice";
import { clearUserState } from "../user/user.slice";
import { isAuthError } from "./invisibly-api.helpers";
import { signOut } from '../user/user.async-thunks';

export const axiosBaseQuery = ({ baseUrl }: { baseUrl: string } = { baseUrl: '' }):
BaseQueryFn<
{
  data?: AxiosRequestConfig['data'];
  body?: AxiosRequestConfig['data'];
  handleErrorGlobally?: boolean;
  localError?: string;
  method: AxiosRequestConfig['method'];
  params?: AxiosRequestConfig['params'];
  successCodes?: number[];
  url: string;
  /** Per-request override (default 60000). Use for long-running jobs. */
  timeout?: number;
},
ApiResponses.BaseSuccessResponse,
UsableError
> => async(arg, { getState, dispatch }) => {
  const { data, body, handleErrorGlobally = true, localError, method, params, successCodes = [], url, timeout: requestTimeoutMs } = arg;
  // Fallback: if arg is mutation payload with month/year (e.g. create PnL), build request body so total_income is sent
  const requestData =
    data ??
    body ??
    (typeof (arg as Record<string, unknown>).month !== 'undefined' &&
    typeof (arg as Record<string, unknown>).year !== 'undefined'
      ? {
        month: (arg as Record<string, unknown>).month,
        year: (arg as Record<string, unknown>).year,
        total_income: (arg as Record<string, unknown>).totalIncome ??
            (arg as Record<string, unknown>).total_income ??
            0,
      }
      : undefined);
  try {
    if (localError) {
      const error = ErrorParsingService.getUsableError(localError);

      if (handleErrorGlobally) {
        dispatch(showNotification({ summary: error.message, theme: 'warning' }));
        ErrorLogService.logError(error, 'baseQuery', 'localError', [url, method, params, requestData], 'function', handleErrorGlobally);
      }

      return { error };
    }

    const { userState: { access } } = getState() as RootState;

    // Check to see if a full url has been passed in. If so, don't use baseUrl
    const checkedUrl = url.includes('https://') ? url : baseUrl + url;
    const axiosConfig: AxiosRequestConfig = {
      data: requestData,
      headers: { Authorization: undefined },
      method,
      params,
      url: checkedUrl,
      timeout: requestTimeoutMs ?? 60000,
    };

    // These calls can't access the global axios headers, so the auth needs to be set here as well
    if (access && axiosConfig.headers) {
      axiosConfig.headers.Authorization = `Bearer ${access}`;
    }

    const result = await axios(axiosConfig);

    // This means it is calling something other than the base invisibly api
    if (checkedUrl === url) {
      // We need to map the data to match the expectation of the baseQuery
      // Maestro handling should be broken out into a different api handler in the future
      return {
        data: { item: result.data },
      };
    }

    const resultData: HttpResponse<any> = result.data;

    if (checkIsNetworkCallSuccess(resultData, successCodes)) {
      return {
        data: result.data,
      };
    }

    // Some endpoints (e.g. b2b/commissions/referrals) return a raw array or non-envelope body; treat 2xx as success
    const status = result.status;
    if (status >= 200 && status < 300 && result.data != null && !isInvisiblyResponse(result.data)) {
      return { data: result.data };
    }

    const error = ErrorParsingService.getUsableError(resultData);
    console.log('is auth error', isAuthError(resultData.status.status_code));
    if (isInvisiblyResponse(result.data)) {
      isAuthError(resultData.status.status_code) && void dispatch(clearUserState());
      const message = typeof resultData.status.status_message === 'string' ? resultData.status.status_message : error.message;
      error.message = `${resultData.status.status_code} - ${message}`;
    }

    if (handleErrorGlobally) {
      dispatch(showNotification({ summary: error.message, theme: 'warning' }));
    }

    ErrorLogService.logError(result.data, 'baseQuery', 'none', [url, method, params, requestData], 'function', handleErrorGlobally);
    return {
      error,
    };
  } catch (axiosError) {

    if ((axiosError as AxiosError)?.response?.status === 401) {
      dispatch(showNotification({ summary: 'Session expired, please log back in', theme: 'warning', life: 10000 }));
      setTimeout(() => {
        dispatch(signOut());
        clearUserState();
        setTimeout(() => {
          window.location.href = '/login';
        }, 500);
      }, 1000);
    }

    const error = ErrorParsingService.getUsableError(axiosError);

    ErrorLogService.logError(axiosError, 'baseQuery', 'none', [url, method, params, requestData], 'function', false);
    return {
      error,
    };
  }
};
